import React, { useState } from "react";
import { addArtwork, updateArtwork } from "../services/firestore";
import { uploadMultipleImages, deleteImage } from "../services/minio";
import { Artwork } from "../types/artwork";
import { getStatusColor, getStatusText } from "../utils";
import "./ArtworkForm.css";

const ArtworkForm: React.FC<{
  artwork?: Artwork;
  onSubmit: (artwork: Artwork) => void;
}> = ({ artwork, onSubmit }) => {
  const [title, setTitle] = useState(artwork?.title || "");
  const [artist, setArtist] = useState(artwork?.artist || "");
  const [location, setLocation] = useState(artwork?.location || "");
  const [institution, setInstitution] = useState(artwork?.institution || "");
  const [contact, setContact] = useState(artwork?.contact || "");
  const [status, setStatus] = useState<"pending" | "in-progress" | "obtained">(
    artwork?.status || "pending"
  );
  const [notes, setNotes] = useState(artwork?.notes || "");
  const [lat, setLat] = useState(artwork?.coordinates?.lat?.toString() || "");
  const [lng, setLng] = useState(artwork?.coordinates?.lng?.toString() || "");
  const [isDigitized, setIsDigitized] = useState(artwork?.isDigitized || false);
  const [loading, setLoading] = useState(false);

  // Image upload states
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>(
    artwork?.images || []
  );
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Check if file is TIFF
  const isTiffFile = (file: File) => {
    return (
      file.type === "image/tiff" ||
      file.type === "image/tif" ||
      file.name.toLowerCase().endsWith(".tiff") ||
      file.name.toLowerCase().endsWith(".tif")
    );
  };

  // Get preview source for image
  const getImagePreviewSrc = (file: File) => {
    if (isTiffFile(file)) {
      // Return a placeholder for TIFF files since most browsers don't support them natively
      return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk3OTdhNyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRJRkY8L3RleHQ+PC9zdmc+";
    }
    return URL.createObjectURL(file);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      // Filter for image files only (including TIFF)
      const imageFiles = filesArray.filter(
        (file) =>
          file.type.startsWith("image/") ||
          file.type === "image/tiff" ||
          file.type === "image/tif" ||
          file.name.toLowerCase().endsWith(".tiff") ||
          file.name.toLowerCase().endsWith(".tif")
      );
      setSelectedFiles((prev) => [...prev, ...imageFiles]);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    // Filter for image files only (including TIFF)
    const imageFiles = files.filter(
      (file) =>
        file.type.startsWith("image/") ||
        file.type === "image/tiff" ||
        file.type === "image/tif" ||
        file.name.toLowerCase().endsWith(".tiff") ||
        file.name.toLowerCase().endsWith(".tif")
    );
    if (imageFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...imageFiles]);
    }
  };

  // Remove selected file before upload
  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove existing image
  const handleRemoveExistingImage = async (imageUrl: string) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      try {
        await deleteImage(imageUrl);
        setExistingImages((prev) => prev.filter((img) => img !== imageUrl));
        alert("Image deleted successfully");
      } catch (error) {
        console.error("Error deleting image:", error);
        alert("Failed to delete image");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Se l'opera è digitalizzata, forza lo status a 'obtained'
    let finalStatus = status;
    if (isDigitized && status !== "obtained") {
      finalStatus = "obtained";
    }

    // Converti coordinate in numeri, con valori di default per la Sicilia
    const coordinates = {
      lat: lat ? parseFloat(lat) : 37.5 + Math.random() * 2,
      lng: lng ? parseFloat(lng) : 13.0 + Math.random() * 2,
    };

    try {
      let artworkId: string;
      let uploadedImageUrls: string[] = [];

      if (artwork?.id) {
        // UPDATE existing artwork
        artworkId = artwork.id;

        // Upload new images if any
        if (selectedFiles.length > 0) {
          setUploadingImages(true);
          try {
            uploadedImageUrls = await uploadMultipleImages(
              selectedFiles,
              artworkId
            );
          } catch (error) {
            console.error("Error uploading images:", error);
            alert("Artwork saved but some images failed to upload");
          } finally {
            setUploadingImages(false);
          }
        }

        // Combine existing and new images
        const allImages = [...existingImages, ...uploadedImageUrls];

        const updatedArtwork: Artwork = {
          id: artwork.id,
          title,
          artist,
          location,
          institution,
          contact,
          status: finalStatus,
          notes,
          isDigitized,
          ...(allImages.length > 0 && { images: allImages }), // Only add images if not empty
          createdAt: artwork.createdAt,
          updatedAt: new Date(),
          coordinates,
        };
        await updateArtwork(artwork.id, updatedArtwork);
        onSubmit(updatedArtwork);
      } else {
        // CREATE new artwork
        const newArtwork: Omit<Artwork, "id"> = {
          title,
          artist,
          location,
          institution,
          contact,
          status: finalStatus,
          notes,
          isDigitized,
          createdAt: new Date(),
          updatedAt: new Date(),
          coordinates,
        };

        // First save artwork to get ID
        artworkId = await addArtwork(newArtwork as Artwork);

        // Then upload images with the artwork ID
        if (selectedFiles.length > 0) {
          setUploadingImages(true);
          try {
            uploadedImageUrls = await uploadMultipleImages(
              selectedFiles,
              artworkId
            );

            // Update artwork with image URLs
            if (uploadedImageUrls.length > 0) {
              await updateArtwork(artworkId, { images: uploadedImageUrls });
            }
          } catch (error) {
            console.error("Error uploading images:", error);
            alert("Artwork created but some images failed to upload");
          } finally {
            setUploadingImages(false);
          }
        }

        onSubmit({
          ...newArtwork,
          id: artworkId,
          images: uploadedImageUrls.length > 0 ? uploadedImageUrls : undefined,
        });
      }

      alert(
        "Artwork saved successfully!" +
          (uploadedImageUrls.length > 0
            ? ` (${uploadedImageUrls.length} image(s) uploaded)`
            : "")
      );
    } catch (error) {
      console.error("Error saving artwork:", error);
      alert("Error saving artwork");
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  return (
    <div className="artwork-form-container">
      {/* Header */}
      <div className="artwork-form-header">
        <div className="artwork-form-header-icon">{artwork ? "✏️" : "🎨"}</div>
        <h1 className="artwork-form-header-title">
          {artwork ? "Edit Artwork" : "Add New Artwork"}
        </h1>
        <p className="artwork-form-header-subtitle">
          {artwork
            ? "Update the artwork details"
            : "Fill in the details to add a new artwork to your collection"}
        </p>
      </div>

      {/* Form */}
      <div className="artwork-form-content">
        <div className="artwork-form-decoration"></div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div className="artwork-form-section">
            <h3 className="artwork-form-section-title">📋 Basic Information</h3>

            <div className="artwork-form-field">
              <label className="artwork-form-label">Artwork Title</label>
              <input
                type="text"
                className="artwork-form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter the artwork title"
                required
                disabled={loading}
              />
            </div>

            <div className="artwork-form-field">
              <label className="artwork-form-label">Artist</label>
              <input
                type="text"
                className="artwork-form-input"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="Enter the artist name"
                required
                disabled={loading}
              />
            </div>

            <div className="artwork-form-grid">
              <div className="artwork-form-field">
                <label className="artwork-form-label">Location</label>
                <input
                  type="text"
                  className="artwork-form-input"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, Sicily"
                  required
                  disabled={loading}
                />
              </div>

              <div className="artwork-form-field">
                <label className="artwork-form-label">Institution</label>
                <input
                  type="text"
                  className="artwork-form-input"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  placeholder="Museum, Gallery, etc."
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="artwork-form-field">
              <label className="artwork-form-label">Contact Information</label>
              <input
                type="text"
                className="artwork-form-input"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Email, phone, or contact person"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Status & Digitization Section */}
          <div className="artwork-form-section">
            <h3 className="artwork-form-section-title">
              📊 Status & Digitization
            </h3>

            <div className="artwork-form-field">
              <label className="artwork-form-label">Current Status</label>
              <div className="artwork-form-status-container">
                <select
                  className="artwork-form-select"
                  value={status}
                  onChange={(e) =>
                    setStatus(
                      e.target.value as "pending" | "in-progress" | "obtained"
                    )
                  }
                  disabled={loading || isDigitized}
                >
                  <option value="pending">📋 Pending</option>
                  <option value="in-progress">⚡ In Progress</option>
                  <option value="obtained">🎯 Obtained</option>
                </select>
                <div
                  className="artwork-form-status-badge"
                  style={{ backgroundColor: getStatusColor(status) }}
                >
                  {getStatusText(status)}
                </div>
              </div>
            </div>

            {/* Digitization Status */}
            <div className="artwork-form-field">
              <label className="artwork-form-label">Digitization Status</label>
              <div
                className={`artwork-form-digitization ${
                  isDigitized ? "digitized" : "not-digitized"
                }`}
              >
                <div className="artwork-form-digitization-header">
                  <div className="artwork-form-digitization-icon">
                    {isDigitized ? "🖼️" : "📸"}
                  </div>
                  <div className="artwork-form-digitization-content">
                    <h4
                      className={`artwork-form-digitization-title ${
                        isDigitized ? "digitized" : "not-digitized"
                      }`}
                    >
                      {isDigitized ? "Already Digitized" : "Not Yet Digitized"}
                    </h4>
                    <p
                      className={`artwork-form-digitization-subtitle ${
                        isDigitized ? "digitized" : "not-digitized"
                      }`}
                    >
                      {isDigitized
                        ? "This artwork has been digitally captured"
                        : "This artwork needs digital photography"}
                    </p>
                  </div>
                </div>

                <div className="artwork-form-digitization-options">
                  <label
                    className={`artwork-form-digitization-option ${
                      !isDigitized ? "active not-digitized" : "inactive"
                    }`}
                  >
                    <input
                      type="radio"
                      name="digitization"
                      checked={!isDigitized}
                      onChange={() => setIsDigitized(false)}
                      disabled={loading}
                    />
                    <span className="artwork-form-digitization-option-icon">
                      📸
                    </span>
                    Not Digitized
                  </label>

                  <label
                    className={`artwork-form-digitization-option ${
                      isDigitized ? "active digitized" : "inactive"
                    }`}
                  >
                    <input
                      type="radio"
                      name="digitization"
                      checked={isDigitized}
                      onChange={() => setIsDigitized(true)}
                      disabled={loading}
                    />
                    <span className="artwork-form-digitization-option-icon">
                      🖼️
                    </span>
                    Already Digitized
                  </label>
                </div>

                <div className="artwork-form-digitization-tip">
                  <strong>💡 Tip:</strong>{" "}
                  {isDigitized
                    ? "Opere digitalizzate sono automaticamente marcate come 'Obtained'. Se l'hai digitalizzata tu, evidentemente l'hai già ottenuta!"
                    : "Questa opzione aiuta a dare priorità alle opere che necessitano ancora di fotografia."}
                </div>
              </div>
            </div>
          </div>

          {/* Location Coordinates Section */}
          <div className="artwork-form-section">
            <h3 className="artwork-form-section-title">
              📍 Geographic Coordinates
            </h3>

            <div className="artwork-form-coordinates">
              <div className="artwork-form-coordinates-grid">
                <div>
                  <label className="artwork-form-label">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    className="artwork-form-input"
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                    placeholder="37.5079"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="artwork-form-label">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    className="artwork-form-input"
                    value={lng}
                    onChange={(e) => setLng(e.target.value)}
                    placeholder="15.0830"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="artwork-form-coordinates-tip">
                <span className="artwork-form-coordinates-tip-icon">💡</span>
                <div className="artwork-form-coordinates-tip-content">
                  <p className="artwork-form-coordinates-tip-title">
                    Optional: Leave empty for random coordinates in Sicily
                  </p>
                  <p className="artwork-form-coordinates-tip-text">
                    Get precise coordinates from{" "}
                    <a
                      href="https://www.google.com/maps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="artwork-form-coordinates-tip-link"
                    >
                      Google Maps →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Images Upload Section */}
          <div className="artwork-form-section">
            <h3 className="artwork-form-section-title">🖼️ Artwork Images</h3>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="artwork-form-field">
                <label className="artwork-form-label">
                  Current Images ({existingImages.length})
                </label>
                <div className="artwork-form-images-grid">
                  {existingImages.map((imageUrl, index) => (
                    <div key={index} className="artwork-form-image-preview">
                      <img src={imageUrl} alt={`Artwork ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(imageUrl)}
                        className="artwork-form-image-remove"
                        disabled={loading}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* File Upload */}
            <div className="artwork-form-field">
              <label className="artwork-form-label">
                {existingImages.length > 0
                  ? "Add More Images"
                  : "Upload Images"}{" "}
                (Optional)
              </label>
              <div
                className={`artwork-form-upload-area ${
                  isDragOver ? "drag-over" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*,.tiff,.tif"
                  multiple
                  onChange={handleFileSelect}
                  disabled={loading}
                  className="artwork-form-file-input"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="artwork-form-upload-label"
                >
                  <div className="artwork-form-upload-icon">
                    {isDragOver ? "📥" : "📁"}
                  </div>
                  <div className="artwork-form-upload-text">
                    <p className="artwork-form-upload-title">
                      {isDragOver
                        ? "Drop images here!"
                        : "Click to select images"}
                    </p>
                    <p className="artwork-form-upload-subtitle">
                      {isDragOver
                        ? "Release to upload"
                        : "or drag and drop here"}
                    </p>
                  </div>
                </label>
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="artwork-form-selected-files">
                  <p className="artwork-form-selected-files-title">
                    Selected: {selectedFiles.length} file
                    {selectedFiles.length > 1 ? "s" : ""}
                  </p>
                  <div className="artwork-form-images-grid">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="artwork-form-image-preview">
                        <img
                          src={getImagePreviewSrc(file)}
                          alt={`Preview ${index + 1}`}
                          onLoad={(e) => {
                            // Only revoke object URL for non-TIFF files
                            if (!isTiffFile(file)) {
                              URL.revokeObjectURL(
                                (e.target as HTMLImageElement).src
                              );
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedFile(index)}
                          className="artwork-form-image-remove"
                          disabled={loading}
                        >
                          ✕
                        </button>
                        <div className="artwork-form-image-name">
                          {file.name}
                          {isTiffFile(file) && (
                            <span className="artwork-form-tiff-indicator">
                              {" "}
                              (TIFF)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadingImages && (
                <div className="artwork-form-upload-progress">
                  <div className="artwork-form-upload-spinner"></div>
                  <span>Uploading images...</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="artwork-form-section">
            <h3 className="artwork-form-section-title">📝 Additional Notes</h3>

            <div className="artwork-form-field">
              <label className="artwork-form-label">Notes (Optional)</label>
              <textarea
                className="artwork-form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional information, description, or special notes about this artwork..."
                disabled={loading}
                rows={5}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="artwork-form-submit">
            <button
              type="submit"
              className="artwork-form-submit-button"
              disabled={loading}
            >
              {loading ? (
                <span className="artwork-form-submit-loading">
                  <span className="artwork-form-submit-spinner"></span>
                  Saving...
                </span>
              ) : (
                <>{artwork ? "💾 Update" : "🎨 Add"} Artwork</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArtworkForm;
